import { createContext, useContext } from 'react';
import {
  useGetTemplateContextClassesQuery,
  useGetTemplateFindingClassesQuery,
  useGetTemplateFlagClassesQuery,
  useGetTemplateFollowUpClassesQuery,
  useGetTemplateSectionTypesQuery,
  useGetWorksheetContextClassesQuery,
  useGetWorksheetFindingClassesQuery,
  useGetWorksheetFlagClassesQuery,
  useGetWorksheetSectionTypesQuery,
} from '@/state/slices/options';

/**
 * The editor UI is shared by worksheets and report templates. They use
 * different class enums on the backend (templates have FOLLOW_UP and a wider
 * flag list), so the option lists come from the endpoint of the kind being
 * edited.
 */
export type EditorKind = 'worksheet' | 'template';

const EditorKindContext = createContext<EditorKind>('worksheet');

export const EditorKindProvider = EditorKindContext.Provider;

const EMPTY: string[] = [];

type OptionsQuery = (
  arg: undefined,
  options: { skip: boolean }
) => { data?: string[] };

/** Runs only the query of the kind being edited. */
function useKindOptions(
  useWorksheetQuery: OptionsQuery,
  useTemplateQuery: OptionsQuery
) {
  const kind = useContext(EditorKindContext);
  const worksheet = useWorksheetQuery(undefined, {
    skip: kind !== 'worksheet',
  });
  const template = useTemplateQuery(undefined, { skip: kind !== 'template' });
  return (kind === 'template' ? template.data : worksheet.data) ?? EMPTY;
}

export const useSectionTypeOptions = () =>
  useKindOptions(
    useGetWorksheetSectionTypesQuery,
    useGetTemplateSectionTypesQuery
  );

export const useContextClassOptions = () =>
  useKindOptions(
    useGetWorksheetContextClassesQuery,
    useGetTemplateContextClassesQuery
  );

export const useFindingClassOptions = () =>
  useKindOptions(
    useGetWorksheetFindingClassesQuery,
    useGetTemplateFindingClassesQuery
  );

export const useFlagClassOptions = () =>
  useKindOptions(
    useGetWorksheetFlagClassesQuery,
    useGetTemplateFlagClassesQuery
  );

/** Worksheets have no follow-up classes, so this is empty there. */
export function useFollowUpClassOptions() {
  const kind = useContext(EditorKindContext);
  const { data } = useGetTemplateFollowUpClassesQuery(undefined, {
    skip: kind !== 'template',
  });
  return (kind === 'template' ? data : undefined) ?? EMPTY;
}
